import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, cn, Icon, Skeleton } from "@nexus/ui";
import type { BigQueryUsageFeatureTotals } from "@/features/bigquery-usage/api/bigquery-usage.api";
import { formatPreciseDurationMs } from "@/features/events/lib/event-timing";
import { SortHeader } from "@/features/identity-hub/components/dashboard/shared/table/SortHeader";
import type { SortState } from "@/features/identity-hub/lib/sort";
import { formatMicrosAsUsd } from "@/features/llm-usage/lib/money";
import { SECONDARY_LIST_MAX_HEIGHT, STICKY_HEAD_CLASS } from "@/features/llm-usage/lib/table-scroll";
import { sortTableRows, toggleUsageSort, type SortValue } from "@/features/admin/lib/table-sort";
import { formatByteShare, formatBytesCompact, formatBytesExact } from "../lib/bytes";
import { serviceForFeature, UNKNOWN_SERVICE } from "../lib/service-rollup";
import { humanizeBigQuerySlug } from "../lib/slug-labels";

interface BigQueryFeatureTableProps {
  rows: BigQueryUsageFeatureTotals[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Rows belong to the previous filter while the next one is in flight. */
  isStale: boolean;
  /** Window total, so each row can show its share of the bytes. */
  totalBytesBilled: number;
  /** From `/features`: which service owns each feature, per KTD8. */
  serviceByFeature: ReadonlyMap<string, string>;
  selectedFeature?: string;
  onSelectFeature: (feature: string) => void;
}

// The rule sits on the cells rather than on `thead`: a collapsed border drawn on
// a sticky section drops out as the rows scroll under it.
const HEADER_BASE =
  "border-b border-border px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground";
// The label column claims every spare pixel, which collapses the numeric columns
// onto their contents.
const HEADER_CLASS = cn(HEADER_BASE, "w-full");
const NUMERIC_HEADER_CLASS = cn(HEADER_BASE, "text-right");
const CELL_CLASS = "px-3 py-2 text-xs text-foreground";
// See the note in `BigQueryTenantTable`: without this a squeezed numeric column
// breaks a value between its number and its unit.
const NUMERIC_CELL_CLASS = cn(CELL_CLASS, "text-right tabular-nums whitespace-nowrap");
const SKELETON_ROWS = 3;
const COLUMN_COUNT = 6;
const DASH = "—";
const PERCENT_FRACTION_DIGITS = 1;
const FRACTION_TO_PERCENT = 100;

type FeatureSortKey =
  | "feature"
  | "queries"
  | "bytesBilled"
  | "costMicros"
  | "cacheHitRate"
  | "avgDurationMs";

/** Bytes, matching the order the server already ranked the rows in. */
const DEFAULT_SORT: SortState<FeatureSortKey> = { key: "bytesBilled", dir: "desc" };
const TEXT_KEYS: ReadonlySet<FeatureSortKey> = new Set<FeatureSortKey>(["feature"]);

/** Sorting by feature sorts by the label on screen, not by the slug behind it. */
function featureSortValue(row: BigQueryUsageFeatureTotals, key: FeatureSortKey): SortValue {
  return key === "feature" ? humanizeBigQuerySlug(row.feature) : row[key];
}

/**
 * Bytes billed per feature, heaviest first: the question this answers is which
 * code path owns the bill, so the row that owns most of it is read first.
 */
export function BigQueryFeatureTable({
  rows,
  isLoading,
  isError,
  onRetry,
  isStale,
  totalBytesBilled,
  serviceByFeature,
  selectedFeature,
  onSelectFeature,
}: BigQueryFeatureTableProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [sort, setSort] = useState<SortState<FeatureSortKey>>(DEFAULT_SORT);

  const sortedRows = useMemo(() => sortTableRows(rows, sort, featureSortValue), [rows, sort]);
  const handleSort = (key: FeatureSortKey) =>
    setSort((prev) => toggleUsageSort(prev, key, TEXT_KEYS));

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-md border border-border bg-card">
      <div className="border-b border-border px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {t("admin.bigQueryUsage.featureTable.title", "By feature")}
      </div>
      <div className={cn(SECONDARY_LIST_MAX_HEIGHT, "overflow-y-auto")}>
        <table className="w-full border-collapse">
          <caption className="sr-only">
            {t("admin.bigQueryUsage.featureTable.caption", "BigQuery usage per feature")}
          </caption>
          <thead className={STICKY_HEAD_CLASS}>
            {/* `group` arms the hover-preview chevron on the inactive columns. */}
            <tr className="group">
              <SortHeader
                id="feature"
                label={t("admin.bigQueryUsage.featureTable.feature", "Feature")}
                sort={sort}
                onSort={handleSort}
                className={HEADER_CLASS}
              />
              <SortHeader
                id="queries"
                label={t("admin.bigQueryUsage.table.queries", "Queries")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="bytesBilled"
                label={t("admin.bigQueryUsage.table.bytesBilled", "Bytes billed")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="costMicros"
                label={t("admin.bigQueryUsage.table.cost", "Cost")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="cacheHitRate"
                label={t("admin.bigQueryUsage.table.cache", "Cache")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
              <SortHeader
                id="avgDurationMs"
                label={t("admin.bigQueryUsage.table.avg", "Avg")}
                sort={sort}
                onSort={handleSort}
                className={NUMERIC_HEADER_CLASS}
              />
            </tr>
          </thead>
          <tbody
            className={cn("transition-opacity duration-200", isStale && "opacity-40")}
            aria-busy={isStale || undefined}
          >
            {/* Checked ahead of the skeleton and ahead of the empty row: a read
                that failed is neither still in flight nor a quiet window. */}
            {isError ? (
              <tr>
                <td className={cn(CELL_CLASS, "py-6 text-center")} colSpan={COLUMN_COUNT}>
                  <span className="flex flex-col items-center gap-2 text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Icon name="alert-circle" size="xs" colorVar="--destructive" aria-hidden />
                      {t(
                        "admin.bigQueryUsage.featureTable.error",
                        "Could not load the feature breakdown.",
                      )}
                    </span>
                    <Button variant="outline" size="sm" onClick={onRetry}>
                      {t("admin.bigQueryUsage.retry", "Try again")}
                    </Button>
                  </span>
                </td>
              </tr>
            ) : isLoading && rows.length === 0 ? (
              Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <tr key={index} className="border-b border-border last:border-0">
                  <td className={CELL_CLASS} colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-4 w-full rounded-sm bg-foreground/8" />
                  </td>
                </tr>
              ))
            ) : sortedRows.length === 0 ? (
              <tr>
                <td
                  className={cn(CELL_CLASS, "py-6 text-center text-muted-foreground")}
                  colSpan={COLUMN_COUNT}
                >
                  {t("admin.bigQueryUsage.table.empty", "No BigQuery queries in this window")}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const service = serviceForFeature(serviceByFeature, row.feature);
                const isUnknownService = service === UNKNOWN_SERVICE;
                const featureLabel = humanizeBigQuerySlug(row.feature);
                const share = formatByteShare(row.bytesBilled, totalBytesBilled, locale);
                const isSelected = row.feature === selectedFeature;

                return (
                  <tr
                    key={row.feature}
                    // The handler lives only here: the cell's button has none of
                    // its own, so a keyboard activation bubbles up to this row
                    // rather than firing the selection twice.
                    onClick={() => onSelectFeature(row.feature)}
                    className={cn(
                      "cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/40",
                      isSelected && "bg-primary/5 hover:bg-primary/5",
                    )}
                  >
                    <td className={CELL_CLASS}>
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        aria-label={
                          isSelected
                            ? t(
                                "admin.bigQueryUsage.table.clearFilter",
                                "Stop filtering by {{name}}",
                                { name: featureLabel },
                              )
                            : t("admin.bigQueryUsage.table.filterBy", "Show only {{name}}", {
                                name: featureLabel,
                              })
                        }
                        className="flex max-w-full flex-col items-start rounded-sm text-left outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        {/* The label is derived from the slug rather than
                            authored, so the slug itself stays one hover away:
                            it is what an engineer greps the logs for. */}
                        <span className="truncate font-medium" title={row.feature}>
                          {featureLabel}
                        </span>
                        {/* KTD8's other half: a feature the registry does not
                            cover says so, rather than showing an empty owner. */}
                        <span
                          className="truncate text-[11px] text-muted-foreground"
                          title={isUnknownService ? undefined : service}
                        >
                          {isUnknownService
                            ? t("admin.bigQueryUsage.table.unknownService", "Unknown service")
                            : humanizeBigQuerySlug(service)}
                        </span>
                      </button>
                    </td>
                    <td className={NUMERIC_CELL_CLASS}>{row.queries.toLocaleString(locale)}</td>
                    <td
                      className={cn(NUMERIC_CELL_CLASS, "font-medium")}
                      title={formatBytesExact(row.bytesBilled, locale)}
                    >
                      <span className="flex items-baseline justify-end gap-1.5">
                        <span>{formatBytesCompact(row.bytesBilled, locale)}</span>
                        {share ? (
                          <span className="text-[11px] font-normal text-muted-foreground">
                            {share}
                          </span>
                        ) : null}
                      </span>
                    </td>
                    {/* A null cost is an unset rate, not a free query: `$0.00`
                        here would be a claim, and the wrong one. */}
                    <td
                      className={cn(NUMERIC_CELL_CLASS, row.costMicros == null && "text-muted-foreground")}
                      title={
                        row.costMicros == null
                          ? t(
                              "admin.bigQueryUsage.table.costUnpricedTitle",
                              "No price per TB is set. Set it in Admin → Settings → Operations.",
                            )
                          : undefined
                      }
                    >
                      {row.costMicros == null ? DASH : formatMicrosAsUsd(row.costMicros, locale)}
                    </td>
                    <td className={cn(NUMERIC_CELL_CLASS, "text-muted-foreground")}>
                      {row.cacheHitRate == null
                        ? DASH
                        : `${(row.cacheHitRate * FRACTION_TO_PERCENT).toLocaleString(locale, {
                            maximumFractionDigits: PERCENT_FRACTION_DIGITS,
                          })}%`}
                    </td>
                    <td className={cn(NUMERIC_CELL_CLASS, "text-muted-foreground")}>
                      {row.avgDurationMs == null ? DASH : formatPreciseDurationMs(row.avgDurationMs)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
